import { logError } from '@ardoq/logging';
import {
  RelationshipDiagramCollapseGroupPayload,
  ViewIdPayload,
} from '../actions';
import type { RelationshipDiagramViewModelStreamState } from '@ardoq/graph';
import { buildViewModel } from './buildViewModel';
import type { ViewDataLimitCheck } from './types';
import type { ViewDataLimitExceeded } from 'views/environmentals/viewDataLimits';
import { WILDCARD } from '@ardoq/common-helpers';
import { isScenarioMode } from 'models/utils/scenarioUtils';
import {
  getAddedAndProcessedNodes,
  getNewCollapsedGroupIdsAfterToggle,
  updateCollapsedGroupsViewSettingsState,
} from './collapseGroupUtils';

/**
 * A collapse rebuild re-runs the traversal, so it re-evaluates the data limit
 * rather than carrying the previous verdict forward.
 */
type StateWithDataLimitCheck = RelationshipDiagramViewModelStreamState & {
  dataLimitCheck: ViewDataLimitCheck;
  dataLimitExceeded: ViewDataLimitExceeded | null;
};

type RebuildViewModelArgs = {
  state: StateWithDataLimitCheck;
  collapsedGroupIds: string[];
  expandedStateChangingGroupId: string;
};
const rebuildViewModel = ({
  state,
  collapsedGroupIds,
  expandedStateChangingGroupId,
}: RebuildViewModelArgs) => {
  const {
    streamState,
    viewSettings,
    viewId,
    viewInterface,
    scopeDiffState,
    dataLimitCheck,
  } = state;

  const viewModel = buildViewModel({
    ...streamState,
    viewInterface,
    viewSettings: { ...viewSettings, collapsedGroupIds },
    expandedStateChangingGroupId,
    shouldCollapseGraph: true,
    viewId,
    isInScopeDiffMode: Boolean(scopeDiffState.scopeDiff),
    scopeDiff: scopeDiffState.scopeDiff,
    i18nLocale: state.i18nLocale,
    dataLimitCheck,
  });
  const { nodes, groups } = viewModel;

  const { addedNodes, updatedNodes, addedGroups, updatedGroups } =
    getAddedAndProcessedNodes({ nodes, groups, expandedStateChangingGroupId });

  return {
    ...viewModel,
    nodes: { ...nodes, add: addedNodes, update: updatedNodes },
    groups: {
      ...groups,
      add: addedGroups,
      update: updatedGroups,
    },
    expandedStateChangingGroupId,
  };
};

export const toggleCollapseGroupReducer = <
  TState extends StateWithDataLimitCheck,
>(
  state: TState,
  { viewId, groupId }: RelationshipDiagramCollapseGroupPayload
) => {
  const {
    viewModel: { groups },
    streamState: { rawGraph },
  } = state;
  const group = groups.byId.get(groupId);
  if (!group) {
    logError(Error('Cannot find group after toggling expansion.'), null, {
      groupId,
      groupIds: Array.from(groups.byId.keys()),
      isScenarioMode: isScenarioMode(),
    });
    return {
      ...state,
      streamState: { ...state.streamState, skipRender: false },
    };
  }

  const collapsedGroupIds = getNewCollapsedGroupIdsAfterToggle({
    oldCollapsedGroupIds: state.viewSettings.collapsedGroupIds,
    allGroupIds: Array.from(rawGraph.groupMap.keys()),
    toggledGroup: group,
  });

  updateCollapsedGroupsViewSettingsState(viewId, collapsedGroupIds);

  const {
    rawGraph: newRawGraph,
    dataLimitExceeded,
    ...viewModel
  } = rebuildViewModel({
    state,
    collapsedGroupIds,
    expandedStateChangingGroupId: group.id,
  });

  return {
    ...state,
    viewSettings: { ...state.viewSettings, collapsedGroupIds },
    viewModel,
    dataLimitExceeded,
    streamState: {
      ...state.streamState,
      rawGraph: newRawGraph,
      skipRender: false,
    },
  };
};

export const collapseAllGroupsReducer = <
  TState extends StateWithDataLimitCheck,
>(
  state: TState,
  { viewId }: ViewIdPayload
): TState => {
  const { viewSettings } = state;
  const collapsedGroupIds = [WILDCARD];
  updateCollapsedGroupsViewSettingsState(viewId, collapsedGroupIds);
  const { dataLimitExceeded, ...viewModel } = rebuildViewModel({
    state,
    collapsedGroupIds,
    expandedStateChangingGroupId: WILDCARD,
  });
  return {
    ...state,
    viewSettings: { ...viewSettings, collapsedGroupIds },
    viewModel,
    dataLimitExceeded,
    streamState: { ...state.streamState, skipRender: false },
  };
};

export const expandAllGroupsReducer = <TState extends StateWithDataLimitCheck>(
  state: TState,
  { viewId }: ViewIdPayload
): TState => {
  const { viewSettings } = state;
  const collapsedGroupIds: string[] = [];
  updateCollapsedGroupsViewSettingsState(viewId, collapsedGroupIds);
  const { dataLimitExceeded, ...viewModel } = rebuildViewModel({
    state,
    collapsedGroupIds,
    expandedStateChangingGroupId: WILDCARD,
  });
  return {
    ...state,
    viewSettings: { ...viewSettings, collapsedGroupIds },
    viewModel,
    dataLimitExceeded,
    streamState: { ...state.streamState, skipRender: false },
  };
};
